import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../../../helpers/api';
import { DEFAULT_ORDER_STATUS, ENDPOINTS } from '../../../../helpers/constants';
import { computeCollectedFees, sumInvoiceLineAmounts } from '../../../../helpers/collectedFees';
import { canViewCollectedFees, isClient, isStaff, validateEmail } from '../../../../helpers/global';
import { onScrollToErrorPosition } from '../../../../helpers/ui';
import useAppChoices from '../../../../hooks/useAppChoices';
import AssignedProcessorsMultiSelectModal from './Common/AssignedProcessorsMultiSelect';
import AttachSupportingDocuments, {
  getPersistedSupportingDocuments
} from '../../../newOrder/Components/AttachSupportingDocuments';
import ClientHoaTextForm from '../../../hoaForm/ClientHoaTextForm';
import HoaForm from '../../../hoaForm/HoaForm';
import { prefetchDirectoryFilesForHoaSlots } from '../../../hoaForm/hoaDirectoryPrefetch';
import {
  applyPreferredToContactList,
  associationToHoaSnapshot,
  blankHoaSnapshot,
  normalizeHoaSnapshot,
  prepareAssociationPayload,
} from '../../../HoaDirectory/HoaDirectoryHelper';
import { serializeClientRequestHoaInfo } from '../../../hoaForm/hoaFormData';

const resolveEntityId = id => (id && typeof id === 'object' ? id._id : id);

const isBlankHoaSlot = hoa => {
  if (!hoa) return true;
  return (
    !String(hoa.name || '').trim() &&
    !resolveEntityId(hoa.associationId) &&
    !String(hoa.managementCompany || '').trim() &&
    !resolveEntityId(hoa.managementCompanyId)
  );
};

const hoaErrorObjectType = [
  { key: 'name', isNotValid: false, type: 'string' },
  { key: 'managementCompany', isNotValid: false, type: 'string' },
  { key: 'email', isNotValid: false, type: 'email' },
  { key: 'companyEmail', isNotValid: false, type: 'email' },
];

let InitialFormFieldError = [
  { key: 'status', isNotValid: false, type: 'string' },
  { key: 'assignedProcessors', isNotValid: false, type: 'array' },
  {
    key: 'homeOwnerInformation',
    type: [{ objectType: [...hoaErrorObjectType] }]
  }
];

const checkIsValid = (
  formData,
  fieldError = [],
  staffMode = false,
  { hoaEditIndex = null, draftOnly = false, editingExistingOrder = false } = {}
) => {
  // Edit HOA on an existing order — skip client-side HOA slot validation; fields are intentionally partial.
  if (editingExistingOrder) {
    const hoaList = formData.homeOwnerInformation || [];
    return {
      result: fieldError.map(field => {
        if (field.key === 'homeOwnerInformation') {
          return {
            key: 'homeOwnerInformation',
            type: hoaList.map(() => ({
              objectType: hoaErrorObjectType.map(o => ({ ...o, isNotValid: false })),
            })),
          };
        }
        return { key: field.key, isNotValid: false, type: field.type };
      }),
      error: false,
    };
  }

  let error = false;
  const validateHoaFieldsOnly = draftOnly || editingExistingOrder;

  return {
    result: fieldError.map(field => {
      const key = field.key;
      const value = formData[key];
      const type = field.type;
      // HOA-only edits hide status / processors — skip those validators.
      if (validateHoaFieldsOnly && key !== 'homeOwnerInformation') {
        return { key, isNotValid: false, type };
      }
      if (type === 'array') {
        if (value.length <= 0) {
          error = true;
          return { key, isNotValid: true, type };
        } else {
          return { key, isNotValid: false, type };
        }
      } else if (type === 'string') {
        if (value === '' || value === null || value === undefined) {
          error = true;
          return { key, isNotValid: true, type };
        } else {
          return { key, isNotValid: false, type };
        }
      } else if (key === 'homeOwnerInformation') {
        const hoaList = formData.homeOwnerInformation || [];
        const skipBlankSlots = draftOnly || editingExistingOrder;

        if (hoaList.length === 0) {
          error = true;
          return { key: 'homeOwnerInformation', type: [] };
        }

        const hasFilledSlot = hoaList.some(hoa => !isBlankHoaSlot(hoa));
        if (!hasFilledSlot) {
          error = true;
        }

        return {
          key: 'homeOwnerInformation',
          type: hoaList.map((hoa, index) => {
            if (hoaEditIndex != null && index !== hoaEditIndex) {
              return {
                objectType: hoaErrorObjectType.map(o => ({ ...o, isNotValid: false })),
              };
            }
            if (skipBlankSlots && isBlankHoaSlot(hoa)) {
              return {
                objectType: hoaErrorObjectType.map(o => ({ ...o, isNotValid: false })),
              };
            }

            const hasName =
              Boolean(String(hoa.name || '').trim()) || Boolean(resolveEntityId(hoa.associationId));
            const hasManagementCompany =
              Boolean(String(hoa.managementCompany || '').trim()) ||
              Boolean(resolveEntityId(hoa.managementCompanyId));

            return {
              objectType: hoaErrorObjectType.map(o => {
                const fieldKey = o.key;
                const fieldTypeName = o.type;
                let isNotValid = false;

                if (fieldKey === 'name' && !hasName) {
                  isNotValid = true;
                  error = true;
                }
                if (
                  fieldKey === 'managementCompany' &&
                  !hoa.noEstoppelNeeded &&
                  hoa.managementType === 'managed' &&
                  !hasManagementCompany
                ) {
                  isNotValid = true;
                  error = true;
                }
                if (fieldKey === 'email' && hoa.managementType !== 'managed') {
                  const trimmed = String(hoa.email || '').trim();
                  if (trimmed && !validateEmail(trimmed)) {
                    isNotValid = true;
                    error = true;
                  }
                }
                if (fieldKey === 'companyEmail' && hoa.managementType === 'managed') {
                  const trimmed = String(hoa.companyEmail || '').trim();
                  if (trimmed && !validateEmail(trimmed)) {
                    isNotValid = true;
                    error = true;
                  }
                }

                return { key: fieldKey, isNotValid, type: fieldTypeName };
              }),
            };
          }),
        };
      } else {
        return { key, isNotValid: false, type };
      }
    }),
    error
  };
};

const HARD_COST_REQUIRED_STATUSES = ['Information Requested', 'Ready for QA', 'Information Received'];

// update the field of the form Field error
const updateOnChangeErrorField = (fieldError = [], key, index, subKey) => {
  return fieldError.map(field => {
    if (field.key === key) {
      if (key === 'homeOwnerInformation') {
        let fieldType = [...field.type];
        fieldType[index] = {
          objectType: fieldType[index].objectType.map(o => {
            if (o.key === subKey) {
              return { ...o, isNotValid: false };
            }
            return o;
          })
        };
        return {
          key,
          type: [...fieldType]
        };
      }
      return { key, isNotValid: false, type: field.type };
    } else {
      return field;
    }
  });
};

const getInitialHomeOwnerInformation = hoa => {
  if (!hoa || hoa.length === 0) {
    return [];
  }
  return hoa.map(h => normalizeHoaSnapshot(h));
};

const EstoppelOrderForm = ({
  onSubmit,
  isEditingOrder,
  loading,
  serviceCreation = false,
  isPublic,
  folderId = null,
  hoaEditIndex = null,
  draftOnly = false,
  enablePreferredContactsForHoaEdit = false,
  onNestedDirectoryModalChange,
}) => {
  const role = useMemo(() => localStorage.getItem('user-role'), []);
  const currentUserInfo = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const canSeeCollectedFees = useMemo(() => canViewCollectedFees(currentUserInfo), [currentUserInfo]);
  const [showAssignedProcessors, setShowAssignedProcesser] = useState(null);
  const [formFieldError, setFormFieldError] = useState(
    !isClient(role) && !isPublic
      ? [...InitialFormFieldError]
      : [...InitialFormFieldError].filter(v => v.key !== 'status' && v.key !== 'assignedProcessors')
  );
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [supportingDocuments, setSupportingDocuments] = useState([]);
  const estoppelStatuses = useAppChoices('Estoppel Status');
  const [hoaDirectoryPrefetches, setHoaDirectoryPrefetches] = useState([]);
  const [hoaDirectoryPrefetchLoading, setHoaDirectoryPrefetchLoading] = useState(false);

  const persistDirectoryPreferredContact = async ({
    associationId,
    contactType,
    contact,
    preferred,
  }) => {
    const id = resolveEntityId(associationId);
    const contactId = resolveEntityId(contact?._id);
    const fail = message => {
      toast.error(message);
      return false;
    };

    if (!id || !contactId) {
      return fail('The contact must be saved before it can be set as preferred.');
    }

    try {
      const { response: association, error: fetchError } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ASSOCIATION_WITH_ID(id),
        method: 'GET',
      });
      if (fetchError || !association) {
        return fail(fetchError || 'Unable to load the association.');
      }

      const directoryHoa = associationToHoaSnapshot(association);
      const contactsKey =
        contactType === 'managementCompany' ? 'managementCompanyContacts' : 'associationContacts';
      const contacts = directoryHoa[contactsKey] || [];
      const directoryContact = contacts.find(item => String(resolveEntityId(item?._id)) === String(contactId));
      if (!directoryContact) {
        return fail('This contact is no longer available in the association directory.');
      }

      const { error: saveError } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ASSOCIATION_WITH_ID(id),
        method: 'PUT',
        requestBody: prepareAssociationPayload({
          ...directoryHoa,
          [contactsKey]: applyPreferredToContactList(contacts, directoryContact, preferred, id),
        }),
      });
      return saveError ? fail(saveError) : true;
    } catch (error) {
      return fail(error?.message || 'Unable to update the preferred contact. Please try again.');
    }
  };
  const [formData, setFormData] = useState(
    isClient(role) || isPublic
      ? {
          homeOwnerInformation: [blankHoaSnapshot()],
          neededDate: '',
          processingFee: isEditingOrder?.processingFee,
          rush: false
        }
      : {
          status: DEFAULT_ORDER_STATUS,
          assignedProcessors: [],
          estimatedTurnAroundTime: '',
          hardCost: '',
          neededDate: '',
          rush: false,
          files: [],
          processingFee: isEditingOrder?.processingFee,
          homeOwnerInformation: [blankHoaSnapshot()]
        }
  );

  const staffMode = isStaff(role) && !isPublic;

  useEffect(() => {
    if (isEditingOrder?._id) {
      setFormData({
        status: isEditingOrder?.status || DEFAULT_ORDER_STATUS,
        assignedProcessors: isEditingOrder?.assignedProcessors || [],
        estimatedTurnAroundTime: isEditingOrder?.estimatedTurnAroundTime
          ? moment(isEditingOrder?.estimatedTurnAroundTime).format('YYYY-MM-DD')
          : '',
        hardCost: isEditingOrder?.hardCost || '',
        processingFee: isEditingOrder?.processingFee || '', // default initialValue
        neededDate: isEditingOrder?.neededDate ? moment(isEditingOrder?.neededDate).format('YYYY-MM-DD') : '',
        rush: isEditingOrder?.rush || false,
        files: [],
        homeOwnerInformation: isEditingOrder?.homeOwnerInformation
          ? getInitialHomeOwnerInformation([...isEditingOrder.homeOwnerInformation])
          : formData.homeOwnerInformation
      });
      isEditingOrder?.homeOwnerInformation &&
        syncHomeOwnerInformationErrorSlots(isEditingOrder.homeOwnerInformation.length);
    }
    return () => {
      InitialFormFieldError = [
        { key: 'status', isNotValid: false, type: 'string' },
        { key: 'assignedProcessors', isNotValid: false, type: 'array' },
        {
          key: 'homeOwnerInformation',
          type: [{ objectType: [...hoaErrorObjectType] }]
        }
      ];
    };
  }, [isEditingOrder?._id]);

  useEffect(() => {
    if (!isEditingOrder?._id) {
      setHoaDirectoryPrefetches([]);
      setHoaDirectoryPrefetchLoading(false);
      return;
    }

    const slots = isEditingOrder.homeOwnerInformation;
    if (!Array.isArray(slots) || !slots.length) {
      setHoaDirectoryPrefetches([]);
      setHoaDirectoryPrefetchLoading(false);
      return;
    }

    let cancelled = false;
    setHoaDirectoryPrefetchLoading(true);

    prefetchDirectoryFilesForHoaSlots(slots)
      .then(prefetches => {
        if (!cancelled) {
          setHoaDirectoryPrefetches(prefetches);
          setHoaDirectoryPrefetchLoading(false);
        }
      })
      .catch(err => {
        console.error(err);
        if (!cancelled) {
          setHoaDirectoryPrefetches(
            slots.map(() => ({ selfInstructionFiles: [], managementCompanyFiles: [] }))
          );
          setHoaDirectoryPrefetchLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // Only re-prefetch when the order changes. Parent often passes a new
    // homeOwnerInformation array after report load, which remounted this form.
  }, [isEditingOrder?._id]);

  const syncHomeOwnerInformationErrorSlots = hoaCount => {
    setFormFieldError(prev =>
      prev.map(field => {
        if (field.key !== 'homeOwnerInformation') return field;
        const type = [...field.type];
        while (type.length < hoaCount) {
          type.push({ objectType: [...hoaErrorObjectType] });
        }
        return { ...field, type: type.slice(0, hoaCount) };
      })
    );
  };

  const isHardCostEmpty = formData.hardCost === '' || formData.hardCost === null || formData.hardCost === undefined;

  const isHardCostInvalid = HARD_COST_REQUIRED_STATUSES.includes(formData.status) && isHardCostEmpty;

  const invoiceLineTotal = useMemo(() => sumInvoiceLineAmounts(isEditingOrder?.feeSourceInvoices), [
    isEditingOrder?.feeSourceInvoices
  ]);

  const collectedFeesDisplay = useMemo(() => {
    if (!isEditingOrder?._id) return null;
    return computeCollectedFees(
      { orderType: 'Estoppel', hardCost: formData.hardCost },
      isEditingOrder.feeSourceInvoices
    );
  }, [isEditingOrder, formData.hardCost]);

  const hasSupportingDocumentUploadIssues = supportingDocuments.some(
    doc => doc.status === 'pending' || doc.status === 'error'
  );

  const persistDirectoryHoa = homeOwnerInformation => {
    setFormData(prev => ({ ...prev, homeOwnerInformation }));
    if (draftOnly || serviceCreation || !isEditingOrder?._id) return;
    onSubmit(
      {
        homeOwnerInformation: homeOwnerInformation.map(({ autoLinked, ...rest }) => rest),
      },
      { close: false }
    );
  };

  const onHandleClickSubmit = e => {
    e.preventDefault();
    if (uploadedFiles.some(f => f.status === 'ERROR')) {
      return toast.error('One or more files have failed uploading, please discard these files and try again!');
    }
    if (supportingDocuments.some(doc => doc.status === 'error')) {
      return toast.error('One or more supporting documents failed to upload. Please remove them and try again.');
    }
    if (supportingDocuments.some(doc => doc.status === 'pending')) {
      return toast.warning('Please wait for supporting documents to finish uploading.');
    }
    const { result, error } = checkIsValid(formData, formFieldError, staffMode, {
      hoaEditIndex,
      draftOnly,
      editingExistingOrder: Boolean(isEditingOrder?._id),
    });
    if (error) {
      setFormFieldError(result);
      onScrollPosition(result);
    } else if (!draftOnly && !isEditingOrder?._id && isHardCostInvalid) {
      setFormFieldError(result);
      onScrollToErrorPosition('hardCost');
    } else {
      setFormFieldError(result);
      const { homeOwnerInformation, ...restFormData } = formData;
      const payload = {
        ...restFormData,
        ...(staffMode
          ? {
              homeOwnerInformation: (homeOwnerInformation || []).map(({ autoLinked, ...rest }) => rest),
            }
          : {
              requestHoaInfo: serializeClientRequestHoaInfo(homeOwnerInformation),
            }),
        assignedProcessors:
          !draftOnly && !isClient(role) && !isPublic
            ? (formData.assignedProcessors || []).map(a => (typeof a === 'object' ? a._id : a))
            : undefined,
        ...(!isEditingOrder?._id && getPersistedSupportingDocuments(supportingDocuments).length
          ? { supportingDocuments: getPersistedSupportingDocuments(supportingDocuments) }
          : {})
      };
      onSubmit(payload);
    }
  };

  const onScrollPosition = result => {
    const fieldArray = result.map(e => {
      if (e.type === 'string' || e.type === 'array') {
        return e;
      } else {
        return e.type
          .map((t, i) => {
            return t.objectType.map(f => {
              return { key: `${f.key}-${i}`, isNotValid: f.isNotValid, type: f.type };
            });
          })
          .flat()
          .find(e => e.isNotValid === true);
      }
    });
    const field = fieldArray.find(e => e && e.isNotValid === true);
    if (field?.key) onScrollToErrorPosition(field.key);
  };

  const onFormDataChange = (field, e) => {
    if (e && e?.target) {
      const updatedFormData = { ...formData };
      updatedFormData[field] = e.target.value;
      setFormData(updatedFormData);
    }
  };

  const onHandleAssignedProcessors = value => {
    setFormData(prevData => ({ ...prevData, assignedProcessors: value }));
    setFormFieldError(updateOnChangeErrorField(formFieldError, 'assignedProcessors'));
  };

  const handleHoaFormChange = homeOwnerInformation => {
    setFormData(prev => ({ ...prev, homeOwnerInformation }));
    setFormFieldError(prev =>
      prev.map(field => {
        if (field.key !== 'homeOwnerInformation') return field;
        const nextLength = homeOwnerInformation.length;
        if (nextLength === 0) {
          return { ...field, type: [] };
        }
        const type = [...field.type];
        while (type.length < nextLength) {
          type.push({ objectType: [...hoaErrorObjectType] });
        }
        return { ...field, type: type.slice(0, nextLength) };
      })
    );
  };

  const isFieldNotValid = (key, index, subKey) => {
    const field = formFieldError.find(f => f.key === key);
    if (!field) return false;
    if (typeof field.type === 'string') {
      return field.isNotValid;
    } else {
      return field.type?.[index]?.objectType?.find(f => f.key === subKey)?.isNotValid;
    }
  };

  return (
    <Container fluid>
      {!isClient(role) && !isPublic && (
        <AssignedProcessorsMultiSelectModal
          show={showAssignedProcessors}
          onHide={() => setShowAssignedProcesser(false)}
          onConfirm={onHandleAssignedProcessors}
          prevData={formData.assignedProcessors}
          isEditing={isEditingOrder?._id}
        />
      )}
      {!isClient(role) && !isPublic && !isEditingOrder?._id && (
        <Row className="align-items-end">
          <Col md={4}>
            <Form.Group className="midFont rounded">
              <Form.Label className="font-weight-bold">
                Status<span className="text-danger">*</span>
              </Form.Label>
              <select
                id="status"
                onChange={e => onFormDataChange('status', e)}
                className={`form-control form-control-sm ${isFieldNotValid('status') && 'input-invalid'}`}
                value={formData.status}
              >
                {estoppelStatuses.map((order, index) => (
                  <option key={index} value={order}>
                    {order}
                  </option>
                ))}
              </select>
              {isFieldNotValid('status') && <p className="xsmallFont text-rush">This is a required Field</p>}
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="midFont">
              <Form.Label>
                Assigned Processor<span className="text-danger">*</span>
              </Form.Label>
              <input
                id="assignedProcessors"
                type="text"
                className={`form-control form-control-sm ${isFieldNotValid('assignedProcessors') && 'input-invalid'}`}
                onClick={() => setShowAssignedProcesser(true)}
                placeholder="select"
                value={
                  formData.assignedProcessors.length > 0
                    ? formData.assignedProcessors.length > 1
                      ? `${formData.assignedProcessors[0]?.name} and ${formData.assignedProcessors.length - 1} others`
                      : formData.assignedProcessors[0]?.name
                    : ''
                }
              />
              {isFieldNotValid('assignedProcessors') && (
                <p className="xsmallFont text-rush">This is a required Field</p>
              )}
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="midFont">
              <Form.Label>Estimated Turnaround Time</Form.Label>
              <input
                type="date"
                onChange={e => onFormDataChange('estimatedTurnAroundTime', e)}
                className="form-control form-control-sm"
                value={formData.estimatedTurnAroundTime}
              />
            </Form.Group>
          </Col>
        </Row>
      )}
      <Row>
        {!isClient(role) && !isPublic && !isEditingOrder?._id && (
          <>
            <Col md={4}>
              <Form.Group className="midFont">
                <Form.Label>
                  Hard Cost
                  {HARD_COST_REQUIRED_STATUSES.includes(formData.status) && <span className="text-danger">*</span>}
                </Form.Label>
                <input
                  id="hardCost"
                  onChange={e => onFormDataChange('hardCost', e)}
                  type="number"
                  value={formData.hardCost}
                  className={`form-control form-control-sm ${isHardCostInvalid ? 'input-invalid' : ''}`}
                />
                {isHardCostInvalid && <p className="xsmallFont text-rush">This is a required Field</p>}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="midFont">
                <Form.Label>Processing Fee</Form.Label>
                <input
                  onChange={e => onFormDataChange('processingFee', e)}
                  type="number"
                  value={formData.processingFee}
                  className="form-control form-control-sm"
                />
              </Form.Group>
            </Col>
            {isEditingOrder?._id && !isClient(role) && canSeeCollectedFees && (
              <Col md={4} className="midFont text-muted">
                <span className="text-dark d-block">Collected fees</span>
                <span className="text-body">
                  {collectedFeesDisplay != null && !Number.isNaN(collectedFeesDisplay)
                    ? `$${Number(collectedFeesDisplay).toFixed(2)}`
                    : 'N/A'}
                </span>
                <div className="xsmallFont text-muted">Total invoice amount: ${invoiceLineTotal.toFixed(2)}</div>
              </Col>
            )}
          </>
        )}
      </Row>
      <div className="mt-3">
        {staffMode ? (
          <HoaForm
            value={formData.homeOwnerInformation}
            onChange={handleHoaFormChange}
            editIndexOnly={hoaEditIndex}
            showCountSelector={hoaEditIndex == null}
            requestHoaInfo={isEditingOrder?.requestHoaInfo}
            prefetchedDirectoryFilesByIndex={hoaDirectoryPrefetches}
            directoryPrefetchLoading={Boolean(isEditingOrder?._id && hoaDirectoryPrefetchLoading)}
            onNestedDirectoryModalChange={onNestedDirectoryModalChange}
            onDirectorySlotsSaved={persistDirectoryHoa}
            showDirectoryPreferredContactToggle={enablePreferredContactsForHoaEdit}
            onDirectoryPreferredContactChange={
              enablePreferredContactsForHoaEdit ? persistDirectoryPreferredContact : undefined
            }
            errors={Object.fromEntries(
              (formData.homeOwnerInformation || []).flatMap((_, i) => [
                [`name-${i}`, isFieldNotValid('homeOwnerInformation', i, 'name')],
                [
                  `managementCompany-${i}`,
                  isFieldNotValid('homeOwnerInformation', i, 'managementCompany')
                ],
                [`email-${i}`, isFieldNotValid('homeOwnerInformation', i, 'email')],
                [`companyEmail-${i}`, isFieldNotValid('homeOwnerInformation', i, 'companyEmail')],
              ])
            )}
          />
        ) : (
          <ClientHoaTextForm
            value={formData.homeOwnerInformation}
            onChange={handleHoaFormChange}
            errors={Object.fromEntries(
              (formData.homeOwnerInformation || []).flatMap((_, i) => [
                [`name-${i}`, isFieldNotValid('homeOwnerInformation', i, 'name')],
                [`email-${i}`, isFieldNotValid('homeOwnerInformation', i, 'email')]
              ])
            )}
          />
        )}
      </div>
      {!isEditingOrder?._id && (
        <Col md={6} className="p-0 justify-content-start">
          <div className="form-check p-0 midFont">
            <label className={`mr-1 form-check-label`}>Rush</label>
            <input
              style={{ height: 15, width: 15 }}
              type="checkbox"
              className="form-check-input ml-2"
              checked={formData.rush}
              onChange={() => onFormDataChange('rush', { target: { value: !formData.rush } })}
            />
            <small className="form-text xmidFont text-muted">*Additional charges may apply for a Rush service</small>
          </div>
        </Col>
      )}
      {!isEditingOrder?._id && ( 
        <Row className="mt-4">
          <Col xs={12} md={6} lg={6} className="pl-md-3">
            <AttachSupportingDocuments
              variant="compact"
              documents={supportingDocuments}
              onDocumentsChange={setSupportingDocuments}
              disabled={loading}
              folderId={folderId}
              orderType="Estoppel"
            />
          </Col>
        </Row>
      )}
      <div className="d-flex justify-content-end align-items-end sticky-bottom my-3">
        <Button
          variant="success"
          className="d-inline-flex align-items-center justify-content-center"
          onClick={onHandleClickSubmit}
          disabled={
            loading ||
            hasSupportingDocumentUploadIssues ||
            uploadedFiles.some(
              f => f.status === 'WAITING_FOR_UPLOAD' || f.status === 'UPLOADING' || f.status === 'ERROR'
            )
          }
        >
          {loading && <Spinner size="sm" animation="border" className="mr-1" />}
          {draftOnly ? 'Save' : !isEditingOrder?._id ? 'Add Estoppel' : 'Update HOA'}
        </Button>
      </div>
    </Container>
  );
};

export default EstoppelOrderForm;
